import React, { useContext } from 'react';
import { BiCollection } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import AppContext from 'Utils/AppContext';
import SourcePicker from 'DataSources/SourcePicker';
import Breadcrumbs from 'Utils/Breadcrumbs';
import { TDM_CONNECTION_RULE } from '../tdmUtils';

/**
 * The connection-selection screen the subset editor swaps in for `?pick=source`. It is a view of
 * the editor, not a route of its own: the editor stays mounted underneath, so the half-typed
 * subset survives the round trip and the chosen connections land straight in its sources.
 *
 * The shared SourcePicker lists every connection and says why the ones TDM cannot read are left
 * out; `connections` (the editor's eligible list) is the fallback when the app context has none.
 */
const SourcePickerView = ({ subsetName, connections, exclude, onAdd, onCancel }) => {
  const ctx = useContext(AppContext) || {};
  const all = ctx.connections && ctx.connections.length ? ctx.connections : connections || [];
  return (
    <div className='tdm-page tdm-text-left' data-testid='tdm-source-picker'>
      <Breadcrumbs
        items={[
          { label: 'Test Data Landscape', to: '/tdm' },
          { label: subsetName || 'New subset', onClick: onCancel },
          { label: 'Add a source' },
        ]}
      />
      <PageHeader
        icon={BiCollection}
        eyebrow='Test Data Management / Subset'
        header='Add a source'
        tooltip='A connection the subset reads from'
        meta={<span>Tick the connections this subset pulls from. If it has no parent yet, the first one added becomes the parent.</span>}
      />
      <SourcePicker
        layout='page'
        selection='multiple'
        sources={all}
        restrict={[TDM_CONNECTION_RULE]}
        exclude={exclude}
        verb='Add sources'
        emptyMessage='Every eligible connection is already a source of this subset.'
        // The picker hands rows back in table order, not click order: the first one added becomes
        // the parent when there is none.
        onConfirm={(rows) => onAdd(rows.map((r) => String(r.id)))}
        onCancel={onCancel}
        testId='tdm-source'
      />
    </div>
  );
};

export default SourcePickerView;
